import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "components/input";
import states from "strings/states.json";
import { getGooglePlacesAddressDetails } from "utils/google-places/address/get-details";
import { mixpanelTrackInteraction } from "utils/tracking/mixpanel";
import { AutocompleteInput } from "../autocomplete-input";
import { Row } from "../styles";
import { useFullAddressFields, type AddressFields } from "../utils";
import { ApartmentInput, StateInput } from "./styles";
import { createFilterOptions } from "@mui/material";

export interface FullAddressFieldsProps {
  initialValue: AddressFields | null;
  onChange: (address: AddressFields | null) => void;
  stateRestriction?: string;
}

export function FullAddressFields({
  stateRestriction,
  ...props
}: FullAddressFieldsProps) {
  const { t } = useTranslation("components", {
    keyPrefix: "addressFields",
  });

  const { fields, handleChange } = useFullAddressFields({
    stateRestriction,
    ...props,
  });

  const zipCodeRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);

  return (
    <>
      <Row>
        <AutocompleteInput
          autoComplete="address-line1"
          inputValue={fields.streetAndNumber.value}
          label={t("streetAndNumber")}
          loading={loading}
          name="street_and_number"
          onChange={async (value) => {
            if (!value?.googlePlacesId) {
              return;
            }

            setLoading(true);

            const googlePlacesAddress = await getGooglePlacesAddressDetails(
              value.googlePlacesId,
              { useSessionToken: true },
            );

            setLoading(false);

            handleChange("city", { value: googlePlacesAddress?.city ?? "" });

            handleChange("postalCode", {
              value: googlePlacesAddress?.postalCode ?? "",
            });

            handleChange("streetAndNumber", {
              value: googlePlacesAddress.street
                ? `${googlePlacesAddress.streetNumber ?? ""} ${
                    googlePlacesAddress.street
                  }`.trim()
                : "",
            });

            mixpanelTrackInteraction("select street from autocomplete", {
              component_name: "address",
              interaction_region: "home address section",
              interaction_type: "text",
              name_of_page: "checkout",
            });
          }}
          onFocus={() => {
            mixpanelTrackInteraction("click street field", {
              component_name: "address",
              interaction_region: "home address section",
              interaction_type: "text",
              name_of_page: "checkout",
            });
          }}
          onInputChange={(value) => {
            handleChange("streetAndNumber", { value });
          }}
          onValidation={(validation) => {
            handleChange("streetAndNumber", { validation });
          }}
          stateRestriction={stateRestriction}
          validate={(value) => !!value}
          value={null}
          variant="default"
        />

        <ApartmentInput
          disabled={loading}
          inputProps={{
            "aria-label": t("apartment.ariaLabel"),
            enterKeyHint: "go",
          }}
          label={t("apartment.label")}
          name="apartment"
          onChange={(event) => {
            handleChange("apartment", { value: event.target.value });
          }}
          onFocus={() => {
            mixpanelTrackInteraction("click apt field", {
              component_name: "address",
              interaction_region: "home address section",
              interaction_type: "text",
              name_of_page: "checkout",
            });
          }}
          onValidation={(validation) => {
            handleChange("apartment", { validation });
          }}
          validate={(value) => value.length <= 7}
          value={fields.apartment.value}
        />
      </Row>

      <Row>
        <Input
          autoComplete="address-level2"
          disabled={loading}
          label={t("city")}
          name="city"
          inputProps={{
            enterKeyHint: "go",
          }}
          onChange={(event) => {
            handleChange("city", { value: event.target.value });
          }}
          onFocus={() => {
            mixpanelTrackInteraction("click city field", {
              component_name: "address",
              interaction_region: "home address section",
              interaction_type: "text",
              name_of_page: "checkout",
            });
          }}
          onValidation={(validation) => {
            handleChange("city", { validation });
          }}
          required
          validate={(value) => value.length > 2}
          value={fields.city.value}
        />
      </Row>

      <Row>
        <StateInput
          disableClearable
          disabled={loading || !!stateRestriction}
          filterOptions={createFilterOptions({
            // Use ZWSP char to separate query parts, so users can't match
            // queries like "new york ny", "new york - ny", etc.
            // eslint-disable-next-line no-irregular-whitespace
            stringify: (option) => `${option.label}​${option.value}`,
          })}
          InputProps={{
            autoComplete: "off",
            error: fields.state.valid === false,
            label: t("state"),
            name: "state",
            required: true,
          }}
          noOptionsText={t("noStateMatches")}
          onBlur={() => {
            if (fields.state.valid && fields.state.value === null) {
              handleChange("state", { valid: false });
            }
          }}
          onChange={(_, value) => {
            handleChange("state", { valid: true, value });
          }}
          onKeyDown={({ code }) => {
            if (
              (code === "Backspace" || code === "Delete") &&
              fields.state.value
            ) {
              handleChange("state", { valid: false });
            }
          }}
          options={states}
          value={fields.state.value}
        />

        <Input
          autoComplete="postal-code"
          disabled={loading}
          inputRef={zipCodeRef}
          label={t("zipCode")}
          name="postal_code"
          inputProps={{
            enterKeyHint: "go",
          }}
          onChange={(event) => {
            handleChange("postalCode", {
              value: event.target.value.replace(/[^\d]/g, "").slice(0, 5),
            });
          }}
          onFocus={() => {
            mixpanelTrackInteraction("click zip code field", {
              component_name: "address",
              interaction_region: "home address section",
              interaction_type: "text",
              name_of_page: "checkout",
            });
          }}
          onValidation={(validation) => {
            handleChange("postalCode", { validation });
          }}
          required
          validate={(value) => /^\d{5}$/.test(value)}
          value={fields.postalCode.value}
        />
      </Row>
    </>
  );
}
